// Simple hash-based router

const routes = {};
let currentRoute = null;

export function registerRoute(path, handler) {
  routes[path] = handler;
}

export function navigateTo(path) {
  window.location.hash = path;
}

export function getCurrentRoute() {
  return currentRoute;
}

export function getHashParams() {
  const hash = window.location.hash.slice(1);
  const [path, queryString] = hash.split("?");
  const params = {};

  if (queryString) {
    queryString.split("&").forEach((param) => {
      const [key, value] = param.split("=");
      params[key] = decodeURIComponent(value);
    });
  }

  return { path, params };
}

export function init() {
  // Handle hash changes
  window.addEventListener("hashchange", handleRoute);

  // Handle initial load
  handleRoute();

  // Update bottom nav active state
  updateBottomNav();
}

async function handleRoute() {
  const { path, params } = getHashParams();
  const routePath = path || "/";

  // Find matching route
  let handler = routes[routePath];

  // Try to match dynamic routes (e.g., /workout/:id)
  if (!handler) {
    for (const [route, routeHandler] of Object.entries(routes)) {
      if (route.includes(":")) {
        const routeRegex = new RegExp(
          "^" + route.replace(/:[^\s/]+/g, "([^/]+)") + "$"
        );
        const match = routePath.match(routeRegex);

        if (match) {
          handler = routeHandler;
          // Extract dynamic params
          const paramNames = route.match(/:[^\s/]+/g).map((p) => p.slice(1));
          paramNames.forEach((name, i) => {
            params[name] = match[i + 1];
          });
          break;
        }
      }
    }
  }

  if (handler) {
    currentRoute = routePath;
    showLoading();
    try {
      await handler(params);
    } catch (err) {
      console.error("Route handler error:", err);
      showToast("An error occurred", "error");
    } finally {
      hideLoading();
      updateBottomNav();
    }
  } else {
    // 404 - redirect to home
    console.warn("Route not found:", routePath);
    navigateTo("/");
  }
}

function updateBottomNav() {
  const { path } = getHashParams();
  const currentPath = path || "/";

  // Remove active class from all nav items
  document.querySelectorAll(".nav-item").forEach((item) => {
    item.classList.remove("active");
  });

  // Add active class to matching nav item
  const activeItem = document.querySelector(
    `.nav-item[href="#${currentPath}"]`
  );
  if (activeItem) {
    activeItem.classList.add("active");
  } else {
    // Check if we're on a sub-page (e.g., /workout/1)
    const basePath = "/" + currentPath.split("/")[1];
    const baseItem = document.querySelector(`.nav-item[href="#${basePath}"]`);
    if (baseItem) {
      baseItem.classList.add("active");
    }
  }
}

// Utility functions
function showLoading() {
  const loading = document.getElementById("loading");
  if (loading) loading.style.display = "flex";
}

function hideLoading() {
  const loading = document.getElementById("loading");
  if (loading) loading.style.display = "none";
}

export function showToast(message, type = "info") {
  const container = document.getElementById("toast-container");
  if (!container) return;

  // Remove any existing toast
  container.innerHTML = "";

  const toast = document.createElement("div");
  toast.className = `toast ${type}`;

  // Icon based on type
  let iconSVG = "";
  if (type === "success") {
    iconSVG = `
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
    `;
  } else if (type === "error") {
    iconSVG = `
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
  } else {
    iconSVG = `
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
    `;
  }

  toast.innerHTML = `
    <div class="toast-icon">
      ${iconSVG}
    </div>
    <div class="toast-message">${message}</div>
  `;

  container.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = "0";
    setTimeout(() => toast.remove(), 200);
  }, 2000);
}

export function showConfirmDialog(message, confirmText = "Delete", cancelText = "Cancel") {
  return new Promise((resolve) => {
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.style.display = 'flex';
    modal.innerHTML = `
      <div class="modal" style="max-width: 400px;">
        <div class="modal-header">
          <h2 class="modal-title">Confirm</h2>
        </div>
        <div class="modal-body">
          <p style="margin: 0;">${message}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" id="confirm-cancel">${cancelText}</button>
          <button class="btn-destructive" id="confirm-action">${confirmText}</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const cleanup = () => {
      modal.remove();
    };

    modal.querySelector('#confirm-cancel').addEventListener('click', () => {
      cleanup();
      resolve(false);
    });

    modal.querySelector('#confirm-action').addEventListener('click', () => {
      cleanup();
      resolve(true);
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        cleanup();
        resolve(false);
      }
    });
  });
}
